import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { AuthContext } from './auth-context';
import {
  AddressPurpose,
  RpcErrorCode,
  getProviderById,
  getProviders,
  request as requestWallet,
} from 'sats-connect';
import axios from 'axios';
import { IProvider } from 'src/types';
import {
  IWalletContext,
  WalletAddress,
  WalletProviderId,
  PersistedWalletState,
} from 'src/types/wallet';
import { setToLocalStorage, getFromLocalStorage, removeFromLocalStorage } from 'src/utils';
import { useNotification } from 'src/hooks';
import { appConfig } from 'src/config/appConfig';

const WALLET_STORAGE_KEY = '::@@omegaoneWalletState@@::';
const XVERSE_PROVIDER_ID = 'XverseProviders.BitcoinProvider';
const LEGACY_XVERSE_PROVIDER_ID = 'xverseProviders.BitcoinProvider';
const MAGIC_EDEN_PROVIDER_ID = 'MagicEdenProviders.BitcoinProvider';
const LEATHER_PROVIDER_ID = 'LeatherProvider';
const PHANTOM_PROVIDER_ID = 'PhantomProviders.BitcoinProvider';
const OKX_PROVIDER_ID = 'OKXProviders.BitcoinProvider';
const ASIGNA_PROVIDER_ID = 'AsignaProvider';

const SIGN_MESSAGE = 'Sign this message to verify your wallet ownership with Omega One.';

const truncateAddress = (address: string): string => {
  if (!address) return '';
  if (address.length <= 12) return address;
  return `${address.slice(0, 5)}...${address.slice(-4)}`;
};

const getXverseProviderId = (): string | null => {
  if (getProviderById(XVERSE_PROVIDER_ID)) return XVERSE_PROVIDER_ID;
  if (getProviderById(LEGACY_XVERSE_PROVIDER_ID)) return LEGACY_XVERSE_PROVIDER_ID;
  if (window.XverseProviders?.BitcoinProvider) return XVERSE_PROVIDER_ID;
  return null;
};

const getMagicEdenProviderId = (): string | null => {
  if (getProviderById(MAGIC_EDEN_PROVIDER_ID)) return MAGIC_EDEN_PROVIDER_ID;
  if (window.magicEden?.bitcoin?.isMagicEden) return MAGIC_EDEN_PROVIDER_ID;
  return null;
};

const getLeatherProviderId = (): string | null => {
  if (getProviderById(LEATHER_PROVIDER_ID)) return LEATHER_PROVIDER_ID;
  if (window.LeatherProvider) return LEATHER_PROVIDER_ID;
  return null;
};

const getPhantomProviderId = (): string | null => {
  if (getProviderById(PHANTOM_PROVIDER_ID)) return PHANTOM_PROVIDER_ID;
  if (window.phantom?.bitcoin?.isPhantom) return PHANTOM_PROVIDER_ID;
  return null;
};

const getOkxProviderId = (): string | null => {
  if (getProviderById(OKX_PROVIDER_ID)) return OKX_PROVIDER_ID;
  if (window.okxwallet?.bitcoin) return OKX_PROVIDER_ID;
  return null;
};

const isWizzAvailable = (): boolean => {
  return typeof window.wizz !== 'undefined';
};

const getAsignaProviderId = (): string | null => {
  if (getProviderById(ASIGNA_PROVIDER_ID)) return ASIGNA_PROVIDER_ID;
  if (window.asigna) return ASIGNA_PROVIDER_ID;
  return null;
};

const registerWalletOnBackend = async (
  walletId: string,
  walletAddress: string,
  publicKey: string,
  signature: string,
  message: string,
): Promise<{ user: { id: string }; accessToken: string }> => {
  const response = await axios.post(`${appConfig.serviceUrl}/auth/connect-wallet`, {
    walletId,
    walletAddress,
    publicKey,
    signature,
    message,
  });
  return response.data;
};

/** Sign a message using sats-connect compatible wallets */
const signWithSatsConnect = async (
  address: string,
  providerId: string,
): Promise<string> => {
  const response = await requestWallet(
    'signMessage',
    { address, message: SIGN_MESSAGE },
    providerId,
  );

  if (response.status === 'success') {
    return response.result.signature;
  }

  const errorResponse = response as { error?: { code?: number; message?: string } };
  if (errorResponse.error?.code === RpcErrorCode.USER_REJECTION) {
    throw new Error('Signature request cancelled by user');
  }
  throw new Error(errorResponse.error?.message || 'Failed to sign message');
};

const noop = () => {
  /* default no-op */
};

export const WalletContext = createContext<IWalletContext>({
  isConnected: false,
  isConnecting: false,
  providerId: null,
  paymentAddress: null,
  ordinalsAddress: null,
  isXverseInstalled: false,
  isUnisatInstalled: false,
  isMagicEdenInstalled: false,
  isLeatherInstalled: false,
  isPhantomInstalled: false,
  isOkxInstalled: false,
  isWizzInstalled: false,
  isAsignaInstalled: false,
  connectXverse: () => Promise.resolve(false),
  connectUnisat: () => Promise.resolve(false),
  connectMagicEden: () => Promise.resolve(false),
  connectLeather: () => Promise.resolve(false),
  connectPhantom: () => Promise.resolve(false),
  connectOkx: () => Promise.resolve(false),
  connectWizz: () => Promise.resolve(false),
  connectAsigna: () => Promise.resolve(false),
  disconnect: noop,
  signMessage: () => Promise.resolve(null),
  displayAddress: '',
});

export const WalletContextProvider = ({ children }: IProvider) => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [providerId, setProviderId] = useState<WalletProviderId | null>(null);
  const [paymentAddress, setPaymentAddress] = useState<WalletAddress | null>(null);
  const [ordinalsAddress, setOrdinalsAddress] = useState<WalletAddress | null>(null);
  const [isXverseInstalled, setIsXverseInstalled] = useState(false);
  const [isUnisatInstalled, setIsUnisatInstalled] = useState(false);
  const [isMagicEdenInstalled, setIsMagicEdenInstalled] = useState(false);
  const [isLeatherInstalled, setIsLeatherInstalled] = useState(false);
  const [isPhantomInstalled, setIsPhantomInstalled] = useState(false);
  const [isOkxInstalled, setIsOkxInstalled] = useState(false);
  const [isWizzInstalled, setIsWizzInstalled] = useState(false);
  const [isAsignaInstalled, setIsAsignaInstalled] = useState(false);

  const { setAuth, clearAuth } = useContext(AuthContext);
  const notify = useNotification();

  useEffect(() => {
    const checkWalletsInstalled = () => {
      // Xverse detection
      const explicitProviderId = getXverseProviderId();
      let hasXverseProvider = false;
      try {
        const providers = getProviders();
        hasXverseProvider = providers.some(
          (p) =>
            p.id === XVERSE_PROVIDER_ID ||
            p.id === LEGACY_XVERSE_PROVIDER_ID,
        );
      } catch {
        // getProviders may throw if no providers exist
      }
      setIsXverseInstalled(Boolean(explicitProviderId || hasXverseProvider));

      // UniSat detection
      setIsUnisatInstalled(typeof window.unisat !== 'undefined');

      // Magic Eden detection
      setIsMagicEdenInstalled(Boolean(getMagicEdenProviderId()));

      // Leather detection
      setIsLeatherInstalled(Boolean(getLeatherProviderId()));

      // Phantom detection
      setIsPhantomInstalled(Boolean(getPhantomProviderId()));

      // OKX detection
      setIsOkxInstalled(Boolean(getOkxProviderId()));

      // Wizz detection
      setIsWizzInstalled(isWizzAvailable());

      // Asigna detection
      setIsAsignaInstalled(Boolean(getAsignaProviderId()));
    };

    const timer = setTimeout(checkWalletsInstalled, 100);
    return () => clearTimeout(timer);
  }, []);

  // Restore wallet state from localStorage on mount.
  // We intentionally skip extension-availability checks here because browser
  // extensions inject asynchronously and are not yet available when this
  // effect runs. The persisted addresses are enough to show the connected
  // state; the extension is only needed later when the user signs a tx.
  useEffect(() => {
    const raw = getFromLocalStorage(WALLET_STORAGE_KEY);
    if (!raw) return;

    try {
      const persisted: PersistedWalletState = JSON.parse(raw);
      if (persisted.providerId && persisted.paymentAddress) {
        setProviderId(persisted.providerId);
        setPaymentAddress(persisted.paymentAddress);
        setOrdinalsAddress(persisted.ordinalsAddress);
      }
    } catch {
      removeFromLocalStorage(WALLET_STORAGE_KEY);
    }
  }, []);

  const persistState = useCallback(
    (pid: WalletProviderId, payment: WalletAddress | null, ordinals: WalletAddress | null) => {
      const state: PersistedWalletState = {
        providerId: pid,
        paymentAddress: payment,
        ordinalsAddress: ordinals,
        connectedAt: Date.now(),
      };
      setToLocalStorage(WALLET_STORAGE_KEY, state);
    },
    [],
  );

  // ── Xverse ──
  const connectXverse = useCallback(async () => {
    const xverseProviderId = getXverseProviderId();

    if (!xverseProviderId) {
      window.open('https://www.xverse.app/download', '_blank');
      notify('Please install the Xverse wallet extension', 'info');
      return false;
    }

    setIsConnecting(true);

    try {
      // Step 1: Connect wallet
      const response = await requestWallet(
        'wallet_connect',
        { addresses: [AddressPurpose.Payment, AddressPurpose.Ordinals] },
        xverseProviderId,
      );

      if (response.status !== 'success') {
        const errorResponse = response as { error?: { code?: number; message?: string } };
        if (errorResponse.error?.code === RpcErrorCode.USER_REJECTION) {
          notify('Connection cancelled by user', 'warning');
        } else {
          notify(errorResponse.error?.message || 'Failed to connect wallet', 'error');
        }
        return false;
      }

      const addresses = response.result.addresses;
      const payment = addresses.find((a) => a.purpose === AddressPurpose.Payment) as WalletAddress | undefined;
      const ordinals = addresses.find((a) => a.purpose === AddressPurpose.Ordinals) as WalletAddress | undefined;
      const address = payment?.address || ordinals?.address;
      const pubKey = payment?.publicKey || ordinals?.publicKey || '';

      if (!address) {
        notify('No address returned from wallet', 'error');
        return false;
      }

      // Step 2: Sign message
      const signature = await signWithSatsConnect(address, xverseProviderId);

      // Step 3: Register on backend + get JWT
      const authResponse = await registerWalletOnBackend('xverse', address, pubKey, signature, SIGN_MESSAGE);
      setAuth(authResponse.accessToken, authResponse.user.id);

      // Step 4: Set local state
      setProviderId('xverse');
      setPaymentAddress(payment || null);
      setOrdinalsAddress(ordinals || null);
      persistState('xverse', payment || null, ordinals || null);

      notify('Wallet connected successfully', 'success');
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred';
      notify(message, 'error');
      return false;
    } finally {
      setIsConnecting(false);
    }
  }, [persistState, notify, setAuth]);

  // ── UniSat ──
  const connectUnisat = useCallback(async () => {
    if (typeof window.unisat === 'undefined') {
      window.open('https://unisat.io/download', '_blank');
      notify('Please install the UniSat wallet extension', 'info');
      return false;
    }

    setIsConnecting(true);

    try {
      // Step 1: Connect wallet
      const accounts = await window.unisat.requestAccounts();
      if (!accounts || accounts.length === 0) {
        notify('No accounts returned from UniSat', 'error');
        return false;
      }

      const publicKey = await window.unisat.getPublicKey();
      const address = accounts[0];

      // Step 2: Sign message
      const signature = await window.unisat.signMessage(SIGN_MESSAGE);

      // Step 3: Register on backend + get JWT
      const authResponse = await registerWalletOnBackend('unisat', address, publicKey, signature, SIGN_MESSAGE);
      setAuth(authResponse.accessToken, authResponse.user.id);

      // Step 4: Set local state
      const walletAddress: WalletAddress = {
        address,
        publicKey,
        purpose: 'payment',
        addressType: address.startsWith('bc1p') || address.startsWith('tb1p') ? 'p2tr' : 'p2wpkh',
      };

      setProviderId('unisat');
      setPaymentAddress(walletAddress);
      setOrdinalsAddress(null);
      persistState('unisat', walletAddress, null);

      notify('Wallet connected successfully', 'success');
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to connect UniSat wallet';
      if (message.includes('User rejected') || message.includes('rejected')) {
        notify('Connection cancelled by user', 'warning');
      } else {
        notify(message, 'error');
      }
      return false;
    } finally {
      setIsConnecting(false);
    }
  }, [persistState, notify, setAuth]);

  // ── Magic Eden ──
  const connectMagicEden = useCallback(async () => {
    const magicEdenProviderId = getMagicEdenProviderId();

    if (!magicEdenProviderId) {
      window.open('https://wallet.magiceden.io/', '_blank');
      notify('Please install the Magic Eden wallet extension', 'info');
      return false;
    }

    setIsConnecting(true);

    try {
      // Step 1: Connect wallet
      const response = await requestWallet(
        'wallet_connect',
        { addresses: [AddressPurpose.Payment, AddressPurpose.Ordinals] },
        magicEdenProviderId,
      );

      if (response.status !== 'success') {
        const errorResponse = response as { error?: { code?: number; message?: string } };
        if (errorResponse.error?.code === RpcErrorCode.USER_REJECTION) {
          notify('Connection cancelled by user', 'warning');
        } else {
          notify(errorResponse.error?.message || 'Failed to connect wallet', 'error');
        }
        return false;
      }

      const addresses = response.result.addresses;
      const payment = addresses.find((a) => a.purpose === AddressPurpose.Payment) as WalletAddress | undefined;
      const ordinals = addresses.find((a) => a.purpose === AddressPurpose.Ordinals) as WalletAddress | undefined;
      const address = payment?.address || ordinals?.address;
      const pubKey = payment?.publicKey || ordinals?.publicKey || '';

      if (!address) {
        notify('No address returned from wallet', 'error');
        return false;
      }

      // Step 2: Sign message
      const signature = await signWithSatsConnect(address, magicEdenProviderId);

      // Step 3: Register on backend + get JWT
      const authResponse = await registerWalletOnBackend('magic-eden', address, pubKey, signature, SIGN_MESSAGE);
      setAuth(authResponse.accessToken, authResponse.user.id);

      // Step 4: Set local state
      setProviderId('magic-eden');
      setPaymentAddress(payment || null);
      setOrdinalsAddress(ordinals || null);
      persistState('magic-eden', payment || null, ordinals || null);

      notify('Wallet connected successfully', 'success');
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred';
      notify(message, 'error');
      return false;
    } finally {
      setIsConnecting(false);
    }
  }, [persistState, notify, setAuth]);

  // ── Leather ──
  const connectLeather = useCallback(async () => {
    const leatherProviderId = getLeatherProviderId();

    if (!leatherProviderId) {
      window.open('https://leather.io/install-extension', '_blank');
      notify('Please install the Leather wallet extension', 'info');
      return false;
    }

    setIsConnecting(true);

    try {
      // Step 1: Connect wallet
      const response = await requestWallet(
        'wallet_connect',
        { addresses: [AddressPurpose.Payment, AddressPurpose.Ordinals] },
        leatherProviderId,
      );

      if (response.status !== 'success') {
        const errorResponse = response as { error?: { code?: number; message?: string } };
        if (errorResponse.error?.code === RpcErrorCode.USER_REJECTION) {
          notify('Connection cancelled by user', 'warning');
        } else {
          notify(errorResponse.error?.message || 'Failed to connect wallet', 'error');
        }
        return false;
      }

      const addresses = response.result.addresses;
      const payment = addresses.find((a) => a.purpose === AddressPurpose.Payment) as WalletAddress | undefined;
      const ordinals = addresses.find((a) => a.purpose === AddressPurpose.Ordinals) as WalletAddress | undefined;
      const address = payment?.address || ordinals?.address;
      const pubKey = payment?.publicKey || ordinals?.publicKey || '';

      if (!address) {
        notify('No address returned from wallet', 'error');
        return false;
      }

      // Step 2: Sign message
      const signature = await signWithSatsConnect(address, leatherProviderId);

      // Step 3: Register on backend + get JWT
      const authResponse = await registerWalletOnBackend('leather', address, pubKey, signature, SIGN_MESSAGE);
      setAuth(authResponse.accessToken, authResponse.user.id);

      // Step 4: Set local state
      setProviderId('leather');
      setPaymentAddress(payment || null);
      setOrdinalsAddress(ordinals || null);
      persistState('leather', payment || null, ordinals || null);

      notify('Wallet connected successfully', 'success');
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred';
      notify(message, 'error');
      return false;
    } finally {
      setIsConnecting(false);
    }
  }, [persistState, notify, setAuth]);

  // ── Phantom ──
  const connectPhantom = useCallback(async () => {
    const phantomBtc = window.phantom?.bitcoin;

    if (!phantomBtc) {
      window.open('https://phantom.app/download', '_blank');
      notify('Please install the Phantom wallet extension', 'info');
      return false;
    }

    setIsConnecting(true);

    try {
      // Step 1: Connect wallet
      const accounts = await phantomBtc.requestAccounts();
      if (!accounts || accounts.length === 0) {
        notify('No accounts returned from Phantom', 'error');
        return false;
      }

      const paymentAccount = accounts.find((a) => a.purpose === 'payment');
      const ordinalsAccount = accounts.find((a) => a.purpose === 'ordinals');
      const primaryAccount = paymentAccount || accounts[0];

      const payment: WalletAddress = {
        address: primaryAccount.address,
        publicKey: primaryAccount.publicKey,
        purpose: 'payment',
        addressType: primaryAccount.addressType === 'p2tr' ? 'p2tr' : 'p2wpkh',
      };

      const ordinals: WalletAddress | null = ordinalsAccount
        ? {
            address: ordinalsAccount.address,
            publicKey: ordinalsAccount.publicKey,
            purpose: 'ordinals',
            addressType: ordinalsAccount.addressType === 'p2tr' ? 'p2tr' : 'p2wpkh',
          }
        : null;

      // Step 2: Sign message
      const encoder = new TextEncoder();
      const messageBytes = encoder.encode(SIGN_MESSAGE);
      const signResult = await phantomBtc.signMessage(primaryAccount.address, messageBytes);
      const signature = btoa(String.fromCharCode(...new Uint8Array(signResult.signature)));

      // Step 3: Register on backend + get JWT
      const authResponse = await registerWalletOnBackend('phantom', payment.address, payment.publicKey, signature, SIGN_MESSAGE);
      setAuth(authResponse.accessToken, authResponse.user.id);

      // Step 4: Set local state
      setProviderId('phantom');
      setPaymentAddress(payment);
      setOrdinalsAddress(ordinals);
      persistState('phantom', payment, ordinals);

      notify('Wallet connected successfully', 'success');
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to connect Phantom wallet';
      if (message.includes('User rejected') || message.includes('rejected')) {
        notify('Connection cancelled by user', 'warning');
      } else {
        notify(message, 'error');
      }
      return false;
    } finally {
      setIsConnecting(false);
    }
  }, [persistState, notify, setAuth]);

  // ── OKX ──
  const connectOkx = useCallback(async () => {
    if (!window.okxwallet?.bitcoin) {
      window.open('https://www.okx.com/download', '_blank');
      notify('Please install the OKX wallet extension', 'info');
      return false;
    }

    setIsConnecting(true);

    try {
      // Step 1: Connect wallet
      const result = await window.okxwallet.bitcoin.connect();
      if (!result?.address) {
        notify('No account returned from OKX', 'error');
        return false;
      }

      const publicKey = result.publicKey || (await window.okxwallet.bitcoin.getPublicKey());
      const address = result.address;

      // Step 2: Sign message
      const signature = await window.okxwallet.bitcoin.signMessage(SIGN_MESSAGE);

      // Step 3: Register on backend + get JWT
      const authResponse = await registerWalletOnBackend('okx', address, publicKey, signature, SIGN_MESSAGE);
      setAuth(authResponse.accessToken, authResponse.user.id);

      // Step 4: Set local state
      const walletAddress: WalletAddress = {
        address,
        publicKey,
        purpose: 'payment',
        addressType: address.startsWith('bc1p') || address.startsWith('tb1p') ? 'p2tr' : 'p2wpkh',
      };

      setProviderId('okx');
      setPaymentAddress(walletAddress);
      setOrdinalsAddress(null);
      persistState('okx', walletAddress, null);

      notify('Wallet connected successfully', 'success');
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to connect OKX wallet';
      if (message.includes('User rejected') || message.includes('rejected')) {
        notify('Connection cancelled by user', 'warning');
      } else {
        notify(message, 'error');
      }
      return false;
    } finally {
      setIsConnecting(false);
    }
  }, [persistState, notify, setAuth]);

  // ── Wizz ──
  const connectWizz = useCallback(async () => {
    if (typeof window.wizz === 'undefined') {
      window.open('https://wizzwallet.io/', '_blank');
      notify('Please install the Wizz wallet extension', 'info');
      return false;
    }

    setIsConnecting(true);

    try {
      // Step 1: Connect wallet
      const accounts = await window.wizz.requestAccounts();
      if (!accounts || accounts.length === 0) {
        notify('No accounts returned from Wizz', 'error');
        return false;
      }

      const publicKey = await window.wizz.getPublicKey();
      const address = accounts[0];

      // Step 2: Sign message
      const signature = await window.wizz.signMessage(SIGN_MESSAGE);

      // Step 3: Register on backend + get JWT
      const authResponse = await registerWalletOnBackend('wizz', address, publicKey, signature, SIGN_MESSAGE);
      setAuth(authResponse.accessToken, authResponse.user.id);

      // Step 4: Set local state
      const walletAddress: WalletAddress = {
        address,
        publicKey,
        purpose: 'payment',
        addressType: address.startsWith('bc1p') || address.startsWith('tb1p') ? 'p2tr' : 'p2wpkh',
      };

      setProviderId('wizz');
      setPaymentAddress(walletAddress);
      setOrdinalsAddress(null);
      persistState('wizz', walletAddress, null);

      notify('Wallet connected successfully', 'success');
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to connect Wizz wallet';
      if (message.includes('User rejected') || message.includes('rejected')) {
        notify('Connection cancelled by user', 'warning');
      } else {
        notify(message, 'error');
      }
      return false;
    } finally {
      setIsConnecting(false);
    }
  }, [persistState, notify, setAuth]);

  // ── Asigna ──
  const connectAsigna = useCallback(async () => {
    const asignaProviderId = getAsignaProviderId();

    if (!asignaProviderId) {
      window.open('https://www.asigna.io/', '_blank');
      notify('Please install the Asigna wallet extension', 'info');
      return false;
    }

    setIsConnecting(true);

    try {
      // Step 1: Connect wallet
      const response = await requestWallet(
        'wallet_connect',
        { addresses: [AddressPurpose.Payment, AddressPurpose.Ordinals] },
        asignaProviderId,
      );

      if (response.status !== 'success') {
        const errorResponse = response as { error?: { code?: number; message?: string } };
        if (errorResponse.error?.code === RpcErrorCode.USER_REJECTION) {
          notify('Connection cancelled by user', 'warning');
        } else {
          notify(errorResponse.error?.message || 'Failed to connect wallet', 'error');
        }
        return false;
      }

      const addresses = response.result.addresses;
      const payment = addresses.find((a) => a.purpose === AddressPurpose.Payment) as WalletAddress | undefined;
      const ordinals = addresses.find((a) => a.purpose === AddressPurpose.Ordinals) as WalletAddress | undefined;
      const address = payment?.address || ordinals?.address;
      const pubKey = payment?.publicKey || ordinals?.publicKey || '';

      if (!address) {
        notify('No address returned from wallet', 'error');
        return false;
      }

      // Step 2: Sign message
      const signature = await signWithSatsConnect(address, asignaProviderId);

      // Step 3: Register on backend + get JWT
      const authResponse = await registerWalletOnBackend('asigna', address, pubKey, signature, SIGN_MESSAGE);
      setAuth(authResponse.accessToken, authResponse.user.id);

      // Step 4: Set local state
      setProviderId('asigna');
      setPaymentAddress(payment || null);
      setOrdinalsAddress(ordinals || null);
      persistState('asigna', payment || null, ordinals || null);

      notify('Wallet connected successfully', 'success');
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred';
      notify(message, 'error');
      return false;
    } finally {
      setIsConnecting(false);
    }
  }, [persistState, notify, setAuth]);

  const disconnect = useCallback(() => {
    setProviderId(null);
    setPaymentAddress(null);
    setOrdinalsAddress(null);
    removeFromLocalStorage(WALLET_STORAGE_KEY);
    clearAuth();
    notify('Wallet disconnected', 'info');
  }, [notify, clearAuth]);

  const signMessage = useCallback(
    async (message: string): Promise<string | null> => {
      const address = paymentAddress?.address || ordinalsAddress?.address;
      if (!providerId || !address) return null;

      try {
        // sats-connect compatible wallets (Xverse, Magic Eden, Leather, Asigna)
        if (['xverse', 'magic-eden', 'leather', 'asigna'].includes(providerId)) {
          let satsProviderId: string | null = null;
          if (providerId === 'xverse') satsProviderId = getXverseProviderId();
          else if (providerId === 'magic-eden') satsProviderId = getMagicEdenProviderId();
          else if (providerId === 'leather') satsProviderId = getLeatherProviderId();
          else if (providerId === 'asigna') satsProviderId = getAsignaProviderId();

          if (!satsProviderId) throw new Error('Wallet extension not found');

          const response = await requestWallet(
            'signMessage',
            { address, message },
            satsProviderId,
          );
          if (response.status === 'success') return response.result.signature;
          throw new Error('Signing failed');
        }

        // UniSat
        if (providerId === 'unisat' && window.unisat) {
          return await window.unisat.signMessage(message);
        }

        // Phantom
        if (providerId === 'phantom' && window.phantom?.bitcoin) {
          const encoder = new TextEncoder();
          const messageBytes = encoder.encode(message);
          const signResult = await window.phantom.bitcoin.signMessage(address, messageBytes);
          return btoa(String.fromCharCode(...new Uint8Array(signResult.signature)));
        }

        // OKX
        if (providerId === 'okx' && window.okxwallet?.bitcoin) {
          return await window.okxwallet.bitcoin.signMessage(message);
        }

        // Wizz
        if (providerId === 'wizz' && window.wizz) {
          return await window.wizz.signMessage(message);
        }

        return null;
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Failed to sign message';
        if (msg.includes('cancelled') || msg.includes('rejected') || msg.includes('User rejected')) {
          notify('Signing cancelled by user', 'warning');
        } else {
          notify(msg, 'error');
        }
        return null;
      }
    },
    [providerId, paymentAddress, ordinalsAddress, notify],
  );

  const isConnected = providerId !== null && (paymentAddress !== null || ordinalsAddress !== null);

  const displayAddress = truncateAddress(
    paymentAddress?.address || ordinalsAddress?.address || '',
  );

  return (
    <WalletContext.Provider
      value={{
        isConnected,
        isConnecting,
        providerId,
        paymentAddress,
        ordinalsAddress,
        isXverseInstalled,
        isUnisatInstalled,
        isMagicEdenInstalled,
        isLeatherInstalled,
        isPhantomInstalled,
        isOkxInstalled,
        isWizzInstalled,
        isAsignaInstalled,
        connectXverse,
        connectUnisat,
        connectMagicEden,
        connectLeather,
        connectPhantom,
        connectOkx,
        connectWizz,
        connectAsigna,
        disconnect,
        signMessage,
        displayAddress,
      }}
    >
      {children}
    </WalletContext.Provider>
  );
};
